import {
  IAgentSettings,
  IChangedSettings,
  IOrderingConfig,
  ISchedule,
} from "../types/agent-settings";
import { IFAQ } from "../types/faq";

const areOrderingConfigEqual = (
  orderingConfig1: IOrderingConfig | null,
  orderingConfig2: IOrderingConfig | null
): boolean => {
  return JSON.stringify(orderingConfig1) === JSON.stringify(orderingConfig2);
};

const areScheduleEqual = (
  schedule1: ISchedule | null,
  schedule2: ISchedule | null
): boolean => {
  if (schedule1 === null || schedule2 === null) return true;
  return Object.keys(schedule1).every((day) => {
    const day1 = schedule1[day as keyof ISchedule];
    const day2 = schedule2[day as keyof ISchedule];
    const filteredDay1 = day1.filter(
      (slot) => slot.open !== null && slot.close !== null
    );
    const filteredDay2 = day2.filter(
      (slot) => slot.open !== null && slot.close !== null
    );
    if (filteredDay1.length !== filteredDay2.length) return false;
    return filteredDay1.every((slot1, index) => {
      const slot2 = filteredDay2[index];
      return slot1.open === slot2.open && slot1.close === slot2.close;
    });
  });
};

const areFaqsEqual = (faqs1: IFAQ[], faqs2: IFAQ[]): boolean => {
  const filterOutEmptyFaqs = (faqs: IFAQ[]) =>
    faqs.filter(
      (faq) => faq.question.trim() !== "" || faq.answer.trim() !== ""
    );

  const filteredFaqs1 = filterOutEmptyFaqs(faqs1);
  const filteredFaqs2 = filterOutEmptyFaqs(faqs2);

  return JSON.stringify(filteredFaqs1) === JSON.stringify(filteredFaqs2);
};

export const hasSettingsChanged = (
  currentSettings: IAgentSettings,
  originalSettings: IAgentSettings
): boolean => {
  const primitiveKeys = Object.keys(currentSettings).filter(
    (key) => !["faqs", "schedule", "orderingConfig"].includes(key)
  );
  if (
    primitiveKeys.some(
      (key) =>
        currentSettings[key as keyof IAgentSettings] !==
        originalSettings[key as keyof IAgentSettings]
    )
  ) {
    return true;
  }

  if (
    !areOrderingConfigEqual(
      currentSettings.orderingConfig,
      originalSettings.orderingConfig
    )
  ) {
    return true;
  }

  if (!areScheduleEqual(currentSettings.schedule, originalSettings.schedule)) {
    return true;
  }

  if (!areFaqsEqual(currentSettings.faqs, originalSettings.faqs)) {
    return true;
  }

  return false;
};

const getChangedOrderingConfig = (
  currentOrderingConfig: IOrderingConfig | null,
  originalOrderingConfig: IOrderingConfig | null
): Partial<IOrderingConfig> => {
  if (!currentOrderingConfig || !originalOrderingConfig) {
    return currentOrderingConfig || {};
  }

  const changedConfig: Partial<IOrderingConfig> = {};
  (Object.keys(currentOrderingConfig) as Array<keyof IOrderingConfig>).forEach(
    (key) => {
      if (currentOrderingConfig[key] !== originalOrderingConfig[key]) {
        (changedConfig[key] as any) = currentOrderingConfig[key];
      }
    }
  );

  return changedConfig;
};

export const getChangedSettings = (
  currentSettings: IAgentSettings,
  originalSettings: IAgentSettings
): IChangedSettings => {
  const changedSettings: IChangedSettings = {};

  // all primitive keys except faqs and schedule
  const primitiveKeys = Object.keys(currentSettings).filter(
    (key) => !["faqs", "schedule", "orderingConfig"].includes(key)
  );
  primitiveKeys.forEach((key) => {
    const typedKey = key as keyof IAgentSettings;
    if (currentSettings[typedKey] !== originalSettings[typedKey]) {
      (changedSettings[typedKey] as any) = currentSettings[typedKey];
    }
  });

  if (!areFaqsEqual(currentSettings.faqs, originalSettings.faqs)) {
    changedSettings.faqs = currentSettings.faqs;
  }

  if (!areScheduleEqual(currentSettings.schedule, originalSettings.schedule)) {
    changedSettings.schedule = currentSettings.schedule;
  }

  if (
    !areOrderingConfigEqual(
      currentSettings.orderingConfig,
      originalSettings.orderingConfig
    )
  ) {
    changedSettings.orderingConfig = getChangedOrderingConfig(
      currentSettings.orderingConfig,
      originalSettings.orderingConfig
    );
  }

  return changedSettings;
};
